// Settings as a reader finds it: a few groups, each opening on its sections.
// Every section keeps the gate its page had and has an address, "<group>" or
// "<group>/<section>", that the URL carries as ?setting=. The ids and their
// order are routeModel's SETTINGS_SECTIONS, which analytics reads too; this
// file adds what a reader sees (names and peeks, in plain English) and who may
// see it. Old links name a page instead (adminView, and for some pages a tab);
// settingFromLegacy turns each into an address. Pure: no React, no reads.
import { PERMISSIONS as P } from "./permissionKeys.js";
import { ENTITLEMENTS, hasEntitlement } from "./entitlements.js";
import { allowsGate } from "./permissions.js";
import { ROUTER_PARAM, SETTINGS_SECTIONS, SETTING_ADDRESSES } from "./routeModel.js";
import { SOURCES } from "../pages/dataSources/model.js";

export const ACCOUNT = "account";

// What each group and section says about itself. `gate` is allowsGate's;
// `entitlement` needs a confirmed grant; `flag` names a build flag the caller
// passes in. `words` are what search finds a section by, beyond its name and
// peek: the labels and values inside it.
const GROUP_COPY = {
    endpoints: { name: "Endpoints", peek: "Installers, enrollment tokens, updates and sensor settings" },
    services: { name: "Connected services", peek: "Connect external integrations" },
    directory: { name: "Identity", peek: "Connect your identity provider" },
    access: { name: "Users and roles", peek: "User management and access control" },
    notifications: { name: "Alert destinations", peek: "Send findings to your external system" },
    "trace-review": { name: "Trace review", peek: "Standards, policy, norms and intents" },
    chat: { name: "Dashboard chat", peek: "Configure the chat within Origin" },
    feedback: { name: "Feedback", peek: "Report a bug or request a feature" },
    [ACCOUNT]: { name: "User settings", peek: "Sign-in, theme and notification history" },
};

const SOURCE_SECTIONS = Object.fromEntries(SOURCES.map((source) => [source.key, {
    name: source.name,
    peek: source.blurb,
    gate: P.USERS_MANAGE,
    flag: source.key === "openai" ? "openai" : undefined,
    words: ["data source", "connector", "compliance"],
}]));

const SECTION_COPY = {
    endpoints: {
        install: { name: "Enroll endpoints", peek: "Create a token, download the installer and run it", gate: { any: [P.PROVISIONING_TOKENS_READ, P.INSTALLERS_READ, P.ENDPOINT_METADATA_READ] },
            words: ["install", "enroll", "connect endpoints", "check-in", "mdm", "valid for", "endpoints it can enroll"] },
        enrolled: { name: "Enrolled endpoints", peek: "Endpoints as they check in, newest first", gate: P.ENDPOINT_METADATA_READ,
            words: ["check-in", "recent endpoints", "first activity", "enrolled"] },
        tokens: { name: "Enrollment tokens", peek: "Every token, how many endpoints each enrolled, and when it expires", gate: P.PROVISIONING_TOKENS_READ,
            words: ["enrollment token", "provisioning token", "revoke", "max uses", "expires"] },
        installers: { name: "Installers", peek: "Every installer for Windows and macOS, by version", gate: P.INSTALLERS_READ,
            words: ["installer", "download", "msi", "exe", "pkg", "sha256", "version"] },
        updates: { name: "Updates", peek: "How new versions reach your endpoints", gate: P.TENANT_CONFIG_READ, entitlement: ENTITLEMENTS.ARTIFACT_ROLLOUT_CONTROL,
            words: ["release", "rollout", "wave", "version", "sensor", "content update", "ringed updates"] },
        "every-endpoint": { name: "Sensor configuration", peek: "The tray icon, and the port Origin listens on", gate: P.TENANT_CONFIG_WRITE,
            words: ["tray icon", "system tray", "port", "4318", "otlp", "collector port", "endpoint configuration"] },
        "browser-chats": { name: "Browser extension", peek: "Chat sites Origin records in users' browsers", gate: P.TENANT_CONFIG_WRITE,
            words: ["browser chat capture", "chatgpt", "claude", "microsoft copilot", "perplexity", "deepseek", "gemini", "browser extension"] },
        collectors: { name: "OTLP mirrors", peek: "Endpoint activity, copied to your own tools", gate: P.TELEMETRY_EXPORT_READ,
            words: ["opentelemetry", "mirror", "collector", "otel", "splunk", "datadog", "logs", "traces", "metrics"] },
    },
    services: SOURCE_SECTIONS,
    directory: {
        directories: { name: "Directory services", peek: "Okta, Microsoft Entra ID or Google Workspace", gate: P.INTEGRATIONS_READ,
            words: ["identity", "okta", "entra", "azure ad", "google workspace", "sync", "integration", "credential"] },
        users: { name: "People", peek: "People your directory services imported", gate: P.DIRECTORY_DATA_READ,
            words: ["users", "directory users", "imported identities", "department", "email"] },
        groups: { name: "Groups", peek: "Groups your directory services imported, and their members", gate: P.DIRECTORY_DATA_READ,
            words: ["directory groups", "members"] },
        devices: { name: "Devices", peek: "Devices your directory services imported, and their owners", gate: P.DIRECTORY_DATA_READ,
            words: ["directory devices", "serial", "owner"] },
        "no-person": { name: "Unmapped endpoints", peek: "Endpoints your directory hasn't matched to a user", gate: { confirmed: P.USERS_MANAGE }, entitlement: ENTITLEMENTS.ABAC,
            words: ["unassigned endpoints", "assignment", "assign a user"] },
    },
    access: {
        people: { name: "Users", peek: "Who can sign in to Origin, and their role", gate: P.USERS_READ,
            words: ["users", "invite", "role", "tenant admin", "secops", "viewer", "remove user", "reset password"] },
        visibility: { name: "Access control", peek: "Which groups can read other users' prompts", gate: { any: [P.GRANTS_READ, P.GRANTS_MANAGE] },
            words: ["grants", "activity access", "prompts and responses", "activity metadata", "enforcement"] },
        requests: { name: "Elevation requests", peek: "Requests to see one user's activity for a few hours", gate: { any: [P.ESCALATIONS_REQUEST, P.ESCALATIONS_APPROVE] },
            words: ["escalations", "access request", "approve", "approvers", "temporary access"] },
    },
    notifications: {
        destinations: { name: "Destinations", peek: "Where findings are sent: Slack, Microsoft Teams or Splunk Cloud", gate: P.INTEGRATIONS_WRITE,
            words: ["notifications", "slack", "teams", "splunk", "webhook", "alerts", "findings", "detections", "triaged trace review"] },
    },
    "trace-review": {
        standards: { name: "Standards", peek: "A change applies to new findings; reviewed ones keep their results", gate: P.TENANT_CONFIG_READ,
            words: ["trace review", "escalation standards", "live", "toggle", "triage", "verifier"] },
        policy: { name: "Policy", peek: "The rules your organization requires of AI work", gate: P.TENANT_CONFIG_READ,
            words: ["trace review", "written policy", "constitution", "rules", "clauses", "compliance", "review profile"] },
        norms: { name: "Norms", peek: "How your people usually work with AI", gate: P.TENANT_CONFIG_READ,
            words: ["trace review", "review profile", "constitution", "prevalence"] },
        intents: { name: "Intents", peek: "What your people use AI to get done", gate: P.TENANT_CONFIG_READ,
            words: ["trace review", "review profile", "constitution", "purpose", "weight"] },
    },
    chat: {
        model: { name: "Default model", peek: "The model Dashboard chat uses", gate: P.LLM_CONFIG_READ,
            words: ["chat model", "llm", "task model", "models and providers"] },
        provider: { name: "Provider and key", peek: "Who runs the model, and the key Origin uses", gate: P.LLM_CONFIG_READ,
            words: ["api key", "anthropic", "openai", "openrouter", "custom", "base url", "gateway"] },
    },
    feedback: {
        report: { name: "Send feedback", peek: "Tell the Origin team what broke or what you need", words: ["bug", "report", "feature request"] },
    },
    [ACCOUNT]: {
        inbox: { name: "Notifications", peek: "Which notifications you get, and every one sent to you", words: ["inbox", "notifications", "notification history", "unread", "dismissed", "bell", "notification settings", "preferences", "mute"] },
        "sign-in": { name: "Sign-in", peek: "How you sign in, and your password", words: ["password", "change password", "single sign-on", "email", "role"] },
        theme: { name: "Theme", peek: "Light or dark", words: ["dark mode", "light mode", "appearance"] },
    },
};

// How a group's sections make its pages. A page lists its sections; with
// `tabs`, each tab holds one or more of them. A section on no page here is a
// page of its own, named as it is. A `merged` tab or page draws its sections
// as one body (its first section's), the others only widening who sees it;
// otherwise each of its sections shows under its own name. Addresses stay the
// sections': an address opens its page at its tab.
const PAGE_LAYOUT = {
    endpoints: [
        { id: "enroll", name: "Enroll endpoints", peek: "Create a token, download the installer, run it and watch endpoints check in", tabs: [
            { id: "setup", name: "Set up", sections: ["install", "enrolled"], merged: true },
            { id: "tokens", name: "Tokens", sections: ["tokens"] },
        ] },
        { id: "sensor", name: "Sensor and browser", peek: "The tray icon and port on every endpoint, and the chat sites the browser extension records", sections: ["every-endpoint", "browser-chats"] },
    ],
    directory: [
        { id: "browse", name: "Directory", peek: "People, groups and devices your directory services imported", tabs: [
            { id: "users", name: "People", sections: ["users"] },
            { id: "groups", name: "Groups", sections: ["groups"] },
            { id: "devices", name: "Devices", sections: ["devices"] },
        ] },
    ],
    "trace-review": [
        { id: "profile", name: "Policy, norms and intents", peek: "Your policy is the rules you've written for AI work, and it outweighs anything Origin observes. Norms are how your people usually work with AI, and intents are what they're using it to get done; Origin learned both from your traces. Review weighs each finding against all three.", tabs: [
            { id: "policy", name: "Policy", sections: ["policy"] },
            { id: "norms", name: "Norms", sections: ["norms"] },
            { id: "intents", name: "Intents", sections: ["intents"] },
        ] },
    ],
    [ACCOUNT]: [
        { id: "profile", name: "Profile", peek: "Who you're signed in as, your theme and your password", sections: ["sign-in", "theme"], merged: true },
    ],
};

// A part of a page (a tab, or the page itself) over the sections the caller
// may see: null when it has none.
function pagePart(part, allowed) {
    const sections = part.sections.map((id) => allowed.get(id)).filter(Boolean);
    if (!sections.length) return null;
    return { id: part.id, name: part.name, merged: Boolean(part.merged), sections, address: sections[0].address };
}

// A group's pages over the sections given, in the order of each page's first
// section. A page or tab the caller can see none of is left out.
function pagesOf(groupId, sections) {
    const allowed = new Map(sections.map((s) => [s.id, s]));
    const layout = PAGE_LAYOUT[groupId] ?? [];
    const placed = new Set(layout.flatMap((page) => (page.tabs ?? [page]).flatMap((part) => part.sections)));
    const pages = [];
    for (const section of sections) {
        if (!placed.has(section.id)) {
            pages.push({ id: section.id, name: section.name, peek: section.peek, tabs: null, merged: false, sections: [section], address: section.address });
            continue;
        }
        const page = layout.find((p) => (p.tabs ?? [p]).some((part) => part.sections.includes(section.id)));
        if (pages.some((p) => p.id === page.id)) continue;
        const tabs = page.tabs ? page.tabs.map((tab) => pagePart(tab, allowed)).filter(Boolean) : null;
        const whole = pagePart({ ...page, sections: (page.tabs ?? [page]).flatMap((part) => part.sections) }, allowed);
        pages.push({ ...whole, peek: page.peek ?? null, merged: Boolean(page.merged), tabs: tabs && tabs.length ? tabs : null, address: (tabs?.[0] ?? whole).address });
    }
    return pages;
}

function buildGroup(id) {
    const sections = SETTINGS_SECTIONS[id].map((sectionId) => ({ id: sectionId, address: `${id}/${sectionId}`, words: [], ...SECTION_COPY[id][sectionId] }));
    return { id, ...GROUP_COPY[id], sections, pages: pagesOf(id, sections) };
}

// The organization's groups, in order; the reader's own are ACCOUNT_GROUP.
export const GROUPS = Object.keys(SETTINGS_SECTIONS).filter((id) => id !== ACCOUNT).map(buildGroup);
export const ACCOUNT_GROUP = buildGroup(ACCOUNT);

// ── Who sees what ────────────────────────────────────────────────

// ctx: { caps, entitlements, flags: { openai } }
export function sectionAllowed(section, { caps, entitlements = null, flags = {} } = {}) {
    if (section.flag && !flags[section.flag]) return false;
    if (section.entitlement && !hasEntitlement(entitlements, section.entitlement)) return false;
    return allowsGate(section.gate ?? null, caps);
}

function withAllowed(group, ctx) {
    const sections = group.sections.filter((section) => sectionAllowed(section, ctx));
    return { ...group, sections, pages: pagesOf(group.id, sections) };
}

// The groups the caller can open, each with only the sections it may see; a
// group with none is left out.
export function visibleGroups(ctx) {
    return GROUPS.map((group) => withAllowed(group, ctx)).filter((group) => group.sections.length);
}

// The reader's own settings, which everyone has.
export function visibleAccount(ctx) {
    return withAllowed(ACCOUNT_GROUP, ctx);
}

// ── Addresses ────────────────────────────────────────────────────

export function settingAddress(group, section = null) {
    return section ? `${group}/${section}` : group;
}

export function parseSetting(value) {
    const [group = "", section = ""] = String(value || "").split("/");
    return { group: group || null, section: section || null };
}

// The page's title for an address: its group's name, or Settings.
export function settingTitle(address) {
    const { group } = parseSetting(address);
    return [...GROUPS, ACCOUNT_GROUP].find((g) => g.id === group)?.name ?? "Settings";
}

// What an address opens among the groups the caller can see: the group and,
// when it names one the caller may see, the section with the page and tab it
// sits on. Anything else is the home.
export function resolveSetting(value, groups, account) {
    const none = { group: null, section: null, page: null, tab: null };
    if (!value || !SETTING_ADDRESSES.includes(value)) return none;
    const { group: groupId, section: sectionId } = parseSetting(value);
    const group = groupId === ACCOUNT ? account : groups.find((g) => g.id === groupId);
    if (!group) return none;
    const section = group.sections.find((s) => s.id === sectionId) ?? null;
    const has = (part) => part.sections.some((s) => s.id === section?.id);
    const page = section ? group.pages?.find(has) ?? null : null;
    return { group, section, page, tab: page?.tabs?.find(has) ?? null };
}

// Old Settings pages, by the id old links carry, and the address each became.
// Some pages kept a tab or a source in their own parameter.
const BROWSER_TABS = new Set(["users", "groups", "devices"]);
const LEGACY_VIEWS = {
    account: () => ACCOUNT,
    report: () => "feedback/report",
    mcp: () => ACCOUNT,
    "chat-models": () => "chat/model",
    users: (p) => (p.get(ROUTER_PARAM.UTAB) === "access" || p.get(ROUTER_PARAM.UTAB) === "activity-access" ? "access/visibility" : "access/people"),
    escalations: () => "access/requests",
    directory: (p) => (BROWSER_TABS.has(p.get(ROUTER_PARAM.DTAB)) ? `directory/${p.get(ROUTER_PARAM.DTAB)}` : "directory/directories"),
    "unassigned-endpoints": () => "directory/no-person",
    "data-sources": (p) => (SETTINGS_SECTIONS.services.includes(p.get("dataSource")) ? `services/${p.get("dataSource")}` : "services"),
    notifications: () => "notifications",
    connect: () => "endpoints/install",
    tokens: () => "endpoints/tokens",
    installers: () => "endpoints/installers",
    "endpoint-config": () => "endpoints/every-endpoint",
    rollouts: () => "endpoints/updates",
};

// The readable names /settings/:view and older links used for pages.
const VIEW_ALIASES = {
    access: "users", rbac: "users", identity: "directory",
    configuration: "endpoint-config", models: "chat-models",
    "mcp-connections": "mcp",
};

// Agent policy left Settings for the Governance mode. The page ids, readable
// names and addresses old links used for it open Governance.
const GOVERNANCE_PATHS = new Set(["agent-policy", "ai-services", "governance", "ai-governance", "policy", "policy/agents"]);

export function opensGovernance(path) {
    return GOVERNANCE_PATHS.has(String(path || "").toLowerCase().replace(/^\/+|\/+$/g, ""));
}

// The address an old page id stands for, or null. `params` carries the page's
// own tab or source, where it had one.
export function settingForView(viewId, params = new URLSearchParams()) {
    const id = String(viewId || "").toLowerCase();
    const resolved = VIEW_ALIASES[id] || id;
    return LEGACY_VIEWS[resolved] ? LEGACY_VIEWS[resolved](params) : null;
}

// The address an old Settings link asks for, or null.
export function settingFromLegacy(searchParams) {
    const view = searchParams.get(ROUTER_PARAM.ADMIN_VIEW) || searchParams.get(ROUTER_PARAM.SETTINGS_VIEW);
    return view ? settingForView(view, searchParams) : null;
}

// A /settings/<group>/<section> path, an address or an old page name, as an
// address; anything else is the home (null).
export function settingForShortcut(path) {
    const value = String(path || "").toLowerCase().replace(/^\/+|\/+$/g, "");
    if (!value) return null;
    if (SETTING_ADDRESSES.includes(value)) return value;
    return settingForView(value);
}

// The parameters an old link used that an address replaces.
export const LEGACY_SETTING_PARAMS = [
    ROUTER_PARAM.ADMIN_VIEW, ROUTER_PARAM.SETTINGS_VIEW, ROUTER_PARAM.UTAB, ROUTER_PARAM.DTAB,
    ROUTER_PARAM.GOVERNANCE_TAB, "dataSource", ROUTER_PARAM.VIEW, "menu",
];

// ── Search ───────────────────────────────────────────────────────

// Sections whose name, peek or words hold every word of the query, best
// first: a name that starts with it, then a name that holds it, then the rest.
/** The search's words, lower-cased: what both searches match and the results mark. */
export function searchTerms(query) {
    return String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
}

/** Groups whose name or peek holds every word of the search, the name's matches first. */
export function searchGroups(query, groups) {
    const terms = searchTerms(query);
    if (!terms.length) return [];
    const whole = terms.join(" ");
    return groups
        .filter((group) => terms.every((term) => `${group.name} ${group.peek}`.toLowerCase().includes(term)))
        .map((group) => {
            const name = group.name.toLowerCase();
            return { group, address: group.id, rank: name.startsWith(whole) ? 0 : name.includes(whole) ? 1 : 2 };
        })
        .sort((a, b) => a.rank - b.rank);
}

export function searchSettings(query, groups) {
    const terms = searchTerms(query);
    if (!terms.length) return [];
    const hits = [];
    for (const group of groups) {
        for (const section of group.sections) {
            const name = section.name.toLowerCase();
            const text = [group.name, section.name, section.peek, ...section.words].join(" ").toLowerCase();
            if (!terms.every((term) => text.includes(term))) continue;
            const whole = terms.join(" ");
            const rank = name.startsWith(whole) ? 0 : name.includes(whole) ? 1 : section.words.some((w) => w.startsWith(whole)) ? 2 : 3;
            hits.push({ group, section, address: section.address, rank });
        }
    }
    return hits.sort((a, b) => a.rank - b.rank);
}
